import { z } from 'zod';
import rawOntology from '../../data/fragrance-signals.v1.json';
import { fragranceDataset } from './catalogue';
import type { Confidence, FragranceRecord } from './types';

const normalise = (value: string) => value.toLocaleLowerCase().trim().replace(/[\s-]+/g, '_');

const fragranceSignalSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  label: z.string().min(2).max(60),
  description: z.string().min(12).max(180),
  supportedAccordTerms: z.array(z.string()).min(1),
  supportedNoteFamilies: z.array(z.string()),
  canonicalNotes: z.array(z.string()),
  negativeTerms: z.array(z.string()),
});

const fragranceSignalOntologySchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  catalogueSchemaVersion: z.literal(fragranceDataset.dataset_metadata.schema_version),
  signals: z.array(fragranceSignalSchema).min(40).max(70),
});

export const fragranceSignalOntology = fragranceSignalOntologySchema.parse(rawOntology);
export type FragranceSignal = z.infer<typeof fragranceSignalSchema>;

const signalIds = fragranceSignalOntology.signals.map((item) => item.id);
const signalIdSet = new Set(signalIds);
const signalById = new Map(fragranceSignalOntology.signals.map((item) => [item.id, item]));
const duplicateIds = signalIds.filter((id, index) => signalIds.indexOf(id) !== index);
if (duplicateIds.length) throw new Error(`Duplicate fragrance signals: ${duplicateIds.join(', ')}`);

export const fragranceSignalIds = signalIds as [string, ...string[]];
export const fragranceSignalIdSchema = z.enum(fragranceSignalIds);
export type FragranceSignalId = z.infer<typeof fragranceSignalIdSchema>;
export const getFragranceSignal = (id: string) => signalById.get(id);
export const isFragranceSignalId = (id: string): id is FragranceSignalId => signalIdSet.has(id);

const catalogueAccords = new Set<string>();
const catalogueNoteFamilies = new Set<string>();
const catalogueNotes = new Set<string>();
for (const fragrance of fragranceDataset.fragrances) {
  fragrance.official_classification.accords.forEach((value) => catalogueAccords.add(normalise(value)));
  fragrance.normalised_notes.forEach((note) => {
    if (note.note_family) catalogueNoteFamilies.add(normalise(note.note_family));
    catalogueNotes.add(normalise(note.canonical_name));
  });
}
for (const signal of fragranceSignalOntology.signals) {
  for (const accord of signal.supportedAccordTerms) {
    if (!catalogueAccords.has(normalise(accord))) {
      throw new Error(`Signal ${signal.id} uses an unsupported accord: ${accord}`);
    }
  }
  for (const family of signal.supportedNoteFamilies) {
    if (!catalogueNoteFamilies.has(normalise(family))) {
      throw new Error(`Signal ${signal.id} uses an unsupported note family: ${family}`);
    }
  }
  for (const note of signal.canonicalNotes) {
    if (!catalogueNotes.has(normalise(note))) {
      throw new Error(`Signal ${signal.id} uses an unsupported canonical note: ${note}`);
    }
  }
  for (const term of signal.negativeTerms) {
    const key = normalise(term);
    if (
      !catalogueAccords.has(key) &&
      !catalogueNoteFamilies.has(key) &&
      !catalogueNotes.has(key)
    ) {
      throw new Error(`Signal ${signal.id} uses an unsupported negative term: ${term}`);
    }
  }
}

export interface FragranceSignalEvidence {
  field:
    | 'official_classification.family'
    | 'official_classification.accords'
    | 'normalised_notes.note_family'
    | 'normalised_notes.canonical_name';
  value: string;
}

export interface DerivedFragranceSignal {
  signalId: FragranceSignalId;
  strength: number;
  evidence: FragranceSignalEvidence[];
  confidence: Confidence;
}

export interface FragranceSignalProfile {
  fragranceId: string;
  ontologyVersion: string;
  signals: DerivedFragranceSignal[];
}

const memo = new WeakMap<FragranceRecord, FragranceSignalProfile>();
const round = (value: number) => Math.round(value * 1000) / 1000;
const confidenceOrder: Confidence[] = ['unknown', 'low', 'medium', 'high'];
const bestConfidence = (values: Confidence[]): Confidence =>
  values.reduce(
    (best, value) => confidenceOrder.indexOf(value) > confidenceOrder.indexOf(best) ? value : best,
    'unknown' as Confidence,
  );

export const deriveFragranceSignalProfile = (record: FragranceRecord): FragranceSignalProfile => {
  const existing = memo.get(record);
  if (existing) return existing;
  const accordValues = new Map(
    record.official_classification.accords.map((value) => [normalise(value), value]),
  );
  const familyValue = record.official_classification.family;
  const noteFamilies = new Map<string, Array<{ value: string; confidence: Confidence }>>();
  const notes = new Map<string, Array<{ value: string; confidence: Confidence }>>();
  for (const note of record.normalised_notes) {
    if (note.note_family) {
      const key = normalise(note.note_family);
      noteFamilies.set(key, [...(noteFamilies.get(key) ?? []), { value: note.note_family, confidence: note.confidence }]);
    }
    const key = normalise(note.canonical_name);
    notes.set(key, [...(notes.get(key) ?? []), { value: note.canonical_name, confidence: note.confidence }]);
  }

  const signals = fragranceSignalOntology.signals
    .map((signal): DerivedFragranceSignal | null => {
      const evidence: FragranceSignalEvidence[] = [];
      const confidences: Confidence[] = [];
      let strength = 0;
      for (const term of signal.supportedAccordTerms) {
        const key = normalise(term);
        const accord = accordValues.get(key);
        if (accord) {
          evidence.push({ field: 'official_classification.accords', value: accord });
          confidences.push(record.official_classification.confidence);
          strength += 0.5;
        }
        if (familyValue && normalise(familyValue) === key) {
          evidence.push({ field: 'official_classification.family', value: familyValue });
          confidences.push(record.official_classification.confidence);
          strength += 0.42;
        }
      }
      for (const family of signal.supportedNoteFamilies) {
        const matches = noteFamilies.get(normalise(family)) ?? [];
        if (matches.length) {
          evidence.push({ field: 'normalised_notes.note_family', value: matches[0]!.value });
          confidences.push(...matches.map((item) => item.confidence));
          strength += 0.32;
        }
      }
      for (const note of signal.canonicalNotes) {
        const matches = notes.get(normalise(note)) ?? [];
        if (matches.length) {
          evidence.push({ field: 'normalised_notes.canonical_name', value: matches[0]!.value });
          confidences.push(...matches.map((item) => item.confidence));
          strength += 0.18;
        }
      }
      const negativeHits = signal.negativeTerms.filter((term) => {
        const key = normalise(term);
        return accordValues.has(key) || noteFamilies.has(key) || notes.has(key);
      }).length;
      strength = Math.max(0, strength - negativeHits * 0.12);
      if (!evidence.length || strength <= 0) return null;
      return {
        signalId: signal.id,
        strength: round(Math.min(1, strength)),
        evidence: evidence.slice(0, 5),
        confidence: bestConfidence(confidences),
      };
    })
    .filter((item): item is DerivedFragranceSignal => Boolean(item))
    .sort((left, right) => right.strength - left.strength || left.signalId.localeCompare(right.signalId));

  const profile = {
    fragranceId: record.fragrance_id,
    ontologyVersion: fragranceSignalOntology.version,
    signals,
  };
  memo.set(record, profile);
  return profile;
};
